import winterfell from '@mc-alberta/winterfell'
import state from '../state'

/**
 * This manages the the Margaery initialization calls. See Margaery's `getAvailableProfiles()` method for details.
 * This will allow for the simultaneous loading of the cersei app while the initialization calls occur in parallel.
 */
const initializer = {
  /**
   * Flag to determine whether the isRecognized and getAllSrcProfiles service was successfully resolved.
   *
   * @type {Boolean}
   */
  profilesFetched: false,

  /**
   * Flag to determine whether the Mastercard middleware /init service was successfully resolved.
   *
   * @type {Boolean}
   */
  transactionUpdated: false,

  /**
   * Any error(s) returned from rejections of `initializeSrcLib`
   */
  initializationError: null,

  /**
   * Any error(s) returned from rejections of `getAvailableProfiles` method.
   */
  profilesFetchedError: null,

  /**
   * Any error(s) returned from rejections of `updateTransaction` method.
   */
  updateTransactionError: null,

  /**
   * Stores available SRC profiles in Margaery state via `getAvailableProfiles()`.
   * Mutates `profilesFetched` flag to `true` once it completes.
   *
   * @returns {Promise}
   */
  async getAvailableProfiles () {
    initializer.profilesFetchedError = null

    try {
      await state.aggregator.src.getSrcProfile() // TODO: confirm this should be called without previously checking if the user is recognized
    } catch (error) {
      initializer.profilesFetchedError = error
    }

    initializer.profilesFetched = true
  },
  /**
   * Stores updated merchant data in Margaery state via `updateTransaction()`.
   * Waits for getAvailableProfiles to have resolved to avoid race condition
   * between get-default-settings and init calls.
   * Mutates `transactionUpdated` flag to `true` once it completes.
   *
   * @returns {Promise}
   */
  async updateTransaction (merchantParams) {
    return new Promise((resolve, reject) => {
      initializer.updateTransactionError = null

      const interval = setInterval(async () => {
        /**
         * If there was an initializationError set while initializing default
         * transaction, updateTransaction will never be called since the profiles
         * would have never have been fetched. In this case we clear the interval
         * immediately and resolve the promise.
         */
        if (initializer.initializationError) {
          clearInterval(interval)
          initializer.transactionUpdated = true
          resolve()
        }
        if (initializer.profilesFetched) {
          clearInterval(interval)
          try {
            await winterfell.updateTransaction(merchantParams)
          } catch (error) {
            initializer.updateTransactionError = error
          }
          initializer.transactionUpdated = true
          resolve()
        }
      })
    })
  },

  /**
   * Returns a promise that resolves once the updateTransaction step has successfully completed.
   * If there were any errors initializingDefaultTransaction or updateTransaction, we reject
   * the promise which in turn will route SRCi to the error page.
   *
   * @returns {Promise<Object>} normalized merchant data
   */
  getNormalizedMerchantData () {
    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        if (initializer.transactionUpdated) {
          clearInterval(interval)
          if (initializer.initializationError) {
            reject(initializer.initializationError)
          } else if (initializer.updateTransactionError) {
            reject(initializer.updateTransactionError)
          } else {
            resolve({
              profiles: state.aggregator.store.getAggregatedProfiles(),
              mastercardCorrelationId: state.aggregator.store.getPriorSrcCorrelationId({ networkId: 'mastercard' }),

              merchant: winterfell.merchantData(),
              /**
               * This stage of the initializer implicitly awaits for all of the SDK's to have been loaded,
               * so here we can deterministically retrieve our list of available networks for SRCi consumption
               */
              availableNetworks: Object.keys(state.aggregator.store.getMethodResults({ methodName: 'init', filter: ([ , { status } ]) => status === 'fulfilled' }))
            })
          }
        }
      })
    })
  }
}

export default initializer
